import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings2, Receipt, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { Stat } from "@/components/gst";
import { HEADS, monthLabel, StatusPill, OpeningDialog, ChallanDialog, PrevMonthBox } from "@/components/gstLedger";

const th = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";
const td = "px-3 py-2 text-sm";

function MonthTable({ rows, onPay, onDel }) {
  if (!rows.length) return <Empty testid="gst-months-empty" text="Koi mahina nahi" />;
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full min-w-[1120px]">
        <thead className="border-b bg-muted/40"><tr>{["Liability Month (N)", "Output GST", "Input ITC", "Net Liability", "Payment Month (N+1)", "Paid", "Balance", "Interest / Late Fee", "Status", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.month} className="border-b align-top last:border-0" data-testid={`gst-month-row-${r.month}`}>
            <td className={`${td} font-semibold`}>{monthLabel(r.month)}{r.opening && <div className="text-[10px] font-bold text-primary">OPENING BALANCE</div>}</td>
            <td className={`${td} amount`}>{r.opening ? "—" : <>{inr(HEADS.reduce((a, h) => a + r.output[h], 0), 2)}<div className="text-[10px] font-normal text-muted-foreground">{HEADS.map((h) => `${h.toUpperCase()} ${inr(r.output[h])}`).join(" · ")}</div></>}</td>
            <td className={`${td} amount`}>{r.opening ? "—" : <>{inr(HEADS.reduce((a, h) => a + r.itc[h] + r.carry_in[h], 0), 2)}<div className="text-[10px] font-normal text-muted-foreground">{HEADS.map((h) => `${h.toUpperCase()} ${inr(r.itc[h] + r.carry_in[h])}`).join(" · ")}</div></>}</td>
            <td className={`${td} amount font-semibold`}>{inr(r.total_liability, 2)}
              <div className="text-[10px] font-normal text-muted-foreground">{HEADS.map((h) => `${h.toUpperCase()} ${inr(r.liability[h])}`).join(" · ")}</div></td>
            <td className={td}>{monthLabel(r.payment_month)}<div className="text-[11px] text-muted-foreground">Due {fmtDate(r.due_date)}</div></td>
            <td className={`${td} amount`}>{inr(r.total_paid, 2)}
              {r.challans.map((c) => <div key={c.id} className="flex items-center gap-1 text-[11px] text-muted-foreground">{fmtDate(c.date)} · {inr(c.amount)}{c.bank_txn_id ? ` · ${c.bank_name || "Bank"}` : ""}
                {!c.bank_txn_id && <button data-testid={`gst-challan-del-${c.id}`} onClick={() => onDel(c)} className="text-rose-600"><Trash2 className="h-3 w-3" /></button>}</div>)}</td>
            <td className={`${td} amount font-semibold ${r.total_balance > 0.5 ? "text-rose-600" : ""}`}>{inr(r.total_balance, 2)}</td>
            <td className={`${td} amount`} data-testid={`gst-penalty-${r.month}`}>{r.interest_paid + r.late_fee_paid > 0 ? <>
              <div className="font-semibold">{inr(r.interest_paid + r.late_fee_paid, 2)}</div>
              <div className="text-[10px] text-muted-foreground">Byaj {inr(r.interest_paid, 2)} · Late fee {inr(r.late_fee_paid, 2)}</div></> : "—"}</td>
            <td className={td}><StatusPill s={r.status} testid={`gst-month-status-${r.month}`} /></td>
            <td className={td}>{!r.running && (r.total_balance > 0.5 || r.status === "Cleared") && <Button size="sm" variant="outline" data-testid={`gst-pay-${r.month}`} onClick={() => onPay(r)}><Receipt className="mr-1 h-3.5 w-3.5" />Challan</Button>}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function HeadLedger({ rows, head }) {
  if (!rows.length) return <Empty testid={`gst-ledger-${head}-empty`} text="Koi entry nahi" />;
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full min-w-[720px]">
        <thead className="border-b bg-muted/40"><tr>{["Date", "Month", "Particulars", "Liability (+)", "Set-off / Paid (−)", "Balance Payable"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-b last:border-0" data-testid={`gst-ledger-${head}-row-${i}`}>
            <td className={td}>{fmtDate(r.date)}</td><td className={td}>{monthLabel(r.month)}</td>
            <td className={td}>{r.particulars}{r.note && <span className="ml-1 text-xs text-muted-foreground">({r.note})</span>}</td>
            <td className={`${td} amount`}>{r.cr ? inr(r.cr, 2) : ""}</td><td className={`${td} amount`}>{r.dr ? inr(r.dr, 2) : ""}</td>
            <td className={`${td} amount font-semibold`}>{inr(r.balance, 2)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

export default function GstLedger() {
  const [d, setD] = useState(null);
  const [openDlg, setOpenDlg] = useState(false);
  const [pay, setPay] = useState(null);
  const load = useCallback(() => { api.get("/gst-ledger/overview").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (c) => {
    if (!window.confirm("Delete this challan?")) return;
    try { await api.delete(`/gst-ledger/challans/${c.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cur = d?.current;
  return (
    <div data-testid="gst-ledger-page">
      <PageHeader title="GST Ledger & Tax Management" subtitle="जीएसटी लेजर और टैक्स मैनेजमेंट · CGST / SGST / IGST liability, challan aur ITC">
        <Button variant="outline" data-testid="gst-opening-btn" onClick={() => setOpenDlg(true)}><Settings2 className="mr-1 h-4 w-4" />Opening Balance</Button>
      </PageHeader>
      {!d ? <p className="text-sm text-muted-foreground">Loading…</p> : <div className="space-y-5">
        {!d.opening.month && <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="gst-no-opening">Opening balance set nahi hai — pichle 12 mahine se hisaab dikh raha hai. Sahi hisaab ke liye "Opening Balance" bharein.</p>}
        <PrevMonthBox r={d.previous} />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          <Stat testid="gst-total-due" label="Total GST baaki (sab pichle mahine)" value={inr(d.total_due, 2)} tone={d.total_due > 0.5 ? "text-rose-600" : "text-emerald-600"} />
          <Stat testid="gst-total-penalty" label="Interest + Late fee bhara" value={inr(d.total_penalty_paid || 0, 2)} />
          <Stat testid="gst-current-running" label={`Is mahine (${monthLabel(cur?.month)}) ki chalti liability`} value={inr(cur?.total_liability || 0, 2)} sub={cur ? `Bharna ${fmtDate(cur.due_date)} tak` : ""} />
          {HEADS.map((h) => <Stat key={h} testid={`gst-itc-${h}`} label={`${h.toUpperCase()} ITC balance`} value={inr(d.itc_balance[h] || 0, 2)} />)}
        </div>
        <Tabs defaultValue="months">
          <TabsList><TabsTrigger value="months" data-testid="tab-gst-months">Month-wise Liability</TabsTrigger>
            {HEADS.map((h) => <TabsTrigger key={h} value={h} data-testid={`tab-gst-${h}`}>{h.toUpperCase()} Ledger</TabsTrigger>)}</TabsList>
          <TabsContent value="months"><MonthTable rows={d.months} onPay={setPay} onDel={del} /></TabsContent>
          {HEADS.map((h) => <TabsContent key={h} value={h}><HeadLedger rows={d.ledgers[h]} head={h} /></TabsContent>)}
        </Tabs>
      </div>}
      <OpeningDialog open={openDlg} onClose={() => setOpenDlg(false)} opening={d?.opening} onSaved={load} />
      <ChallanDialog month={pay} onClose={() => setPay(null)} onSaved={load} />
    </div>
  );
}
